import React, { useEffect, useState } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import { useDispatch, useSelector } from "react-redux";
import {
  addRateService,
  getProductRateService /* submitReviewService*/,
} from "redux/services/general_service";
import { useTranslation } from "react-i18next";

const CustomerReviews = ({
  productId,
  reviews = [],
  productRating,
  totalReviews,
  metaProductRate,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isLoadingProductRate } = useSelector((state) => state.general);

  const [sortBy, setSortBy] = useState("newest");
  const [filterBy, setFilterBy] = useState("all");
  const [showAll, setShowAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [localReviews, setLocalReviews] = useState([]);
  const [newReview, setNewReview] = useState({ rate: 5, comment: "" }); // New review state
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadMoreReviews = async () => {
    const nextPage = currentPage + 1;
    await dispatch(
      getProductRateService({ id: productId, page: nextPage })
    ).then((response) => {
      if (response?.payload?.status === 200) {
        setLocalReviews((prevReviews) => [
          ...prevReviews,
          ...response?.payload?.data?.data,
        ]);
        setCurrentPage(nextPage);
      }
    });
  };

  const handleReviewChange = (e) => {
    const { name, value } = e.target;
    setNewReview((prev) => ({ ...prev, [name]: value }));
  };

  const handleRatingChange = (rate) => {
    setNewReview((prev) => ({ ...prev, rate }));
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await dispatch(
        addRateService({ product_id: productId, ...newReview })
      );
      if (response?.payload?.status === 200) {
        // Reset the review form
        setNewReview({ rate: 5, comment: "" });
        // Optionally, you can reload reviews or add the new review to the local state
        loadMoreReviews();
        dispatch(getProductRateService({ id: productId, page: 1 })).then(
          (response) => {
            if (response?.payload?.status === 200) {
              setLocalReviews(response?.payload?.data?.data);
              setCurrentPage(1);
            }
          }
        );
      }
    } catch (error) {
      console.error("Error submitting review:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const sortedAndFilteredReviews = () => {
    let filtered = reviews;

    // Filter by rate
    if (filterBy !== "all") {
      filtered = filtered.filter(
        (review) => review.rate === parseInt(filterBy)
      );
    }

    // Sort reviews
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "newest":
          return new Date(b.date) - new Date(a.date);
        case "oldest":
          return new Date(a.date) - new Date(b.date);
        case "highest":
          return b.rate - a.rate;
        case "lowest":
          return a.rate - b.rate;
        case "helpful":
          return b.helpfulCount - a.helpfulCount;
        default:
          return 0;
      }
    });

    return showAll ? filtered : filtered.slice(0, 5);
  };

  const renderStars = (rate) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <Icon
          key={i}
          name="Star"
          size={16}
          className={
            i <= rate ? "text-yellow-400 fill-current" : "text-gray-300"
          }
          onClick={() => handleRatingChange(i)} // Allow clicking to set rate
        />
      );
    }
    return stars;
  };

  useEffect(() => {
    dispatch(getProductRateService({ id: productId, page: 1 })).then(
      (response) => {
        if (response?.payload?.status === 200) {
          setLocalReviews(response?.payload?.data?.data);
          setCurrentPage(1);
        }
      }
    );
  }, [dispatch, productId]);

  return (
    <div className="space-y-6">
      {/* Reviews Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-text-primary">
            {t("customerReviews")}
          </h2>
          <p className="text-text-secondary">
            {t("show")} {localReviews.length} {t("of")} {totalReviews}{" "}
    
          </p>
        </div>
        <button
          variant="outline"
          iconName="Edit"
          iconPosition="left"
          onClick={() => setShowAll(!showAll)}
            className="bg-[#FF9F00] text-global-8 rounded-[4px] px-4 sm:px-6 lg:px-7 py-1 sm:py-2.5 lg:py-1.5"
        >
          {t("writeReview")}
        </button>
      </div>

      {/* Review Submission Form */}
      <form
        onSubmit={handleSubmitReview}
        className="bg-surface border border-border rounded-[12px] p-6"
      >
        <h3 className="text-lg font-semibold text-text-primary mb-4">
          {t("writeReview")}
        </h3>
        <div className="flex items-center mb-4">
          {renderStars(newReview.rate)} {/* Render star rate */}
        </div>
        <textarea
          name="comment"
          value={newReview.comment}
          onChange={handleReviewChange}
       //   placeholder={t("writeYourReview")}
          className="w-full border border-border rounded-[4px] p-2"
          rows="4"
          required
        />
        <div className="flex justify-end mt-4">
          <button   className="bg-[#FF9F00] text-global-8 rounded-[4px] px-4 sm:px-6 lg:px-7 py-1 sm:py-2.5 lg:py-1.5" type="submit" variant="default" loading={isSubmitting}>
            {t("send")}
          </button>
        </div>
      </form>

      {/* Reviews List */}
      <div className="space-y-6">
        {localReviews?.map((review) => (
          <div
            key={review.id}
            className="bg-surface border border-border rounded-[12px] p-6"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Icon name="User " size={20} className="text-primary" />
                </div>
                <div>
                  <h4 className="font-medium text-text-primary">
                    {review?.userName}
                  </h4>
                  {/*  <div className="flex items-center space-x-2 text-sm text-text-secondary">
                    <span>{new Date(review?.date).toLocaleDateString()}</span>
                    <span>•</span>
                    <span>{getTimeAgo(review?.date)}</span>
                  </div>*/}
                </div>
              </div>
              <div className="flex items-center space-x-1">
                {renderStars(review?.rate)}
              </div>
            </div>
            <p className="text-text-secondary mb-4 leading-relaxed">
              {review?.comment}
            </p>
          </div>
        ))}
      </div>

      {/* Load More Button */}
      {!showAll && currentPage < metaProductRate?.last_page && (
        <div className="text-center">
          <Button
            variant="outline"
            onClick={loadMoreReviews}
            iconName="ChevronDown"
            iconPosition="right"
          >
            {isLoadingProductRate ? t("loading") : t("loadMore")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default CustomerReviews;
